/**
 * ROBUST COLUMN MATCHING UTILITY
 * 
 * This utility provides centralized, robust column matching functionality
 * to prevent issues with CSV column name variations that can cause
 * filtering and analytics problems.
 * 
 * ISSUES THIS PREVENTS:
 * - Trailing spaces in column names (e.g., "Website Engagement ")
 * - Case sensitivity issues
 * - Leading spaces
 * - Multiple spaces
 * - Different implementations across components
 */

export interface ColumnMatchOptions {
  /** Whether to include debug logging */
  debug?: boolean;
  /** Custom field variations to try */
  customVariations?: string[];
}

/**
 * Get a field value from a row with robust column name matching
 * 
 * @param row - The data row object
 * @param possibleFields - Array of possible field names to match
 * @param options - Optional configuration
 * @returns The matched value or null
 */
export const getFieldValue = (
  row: any, 
  possibleFields: readonly string[] | string[], 
  options: ColumnMatchOptions = {}
): any => {
  const { debug = false, customVariations = [] } = options;
  
  for (const field of possibleFields) {
    // Try exact match first
    let value = row[field];
    if (value && value !== 'N/A' && value !== '') {
      if (debug) console.log(`✅ Exact match for "${field}":`, value);
      return value;
    }
    
    // Try variations: lowercase, uppercase, trimmed, trimmed lowercase, trimmed uppercase
    const variations = [
      field.toLowerCase(),
      field.toUpperCase(),
      field.trim(),
      field.trim().toLowerCase(),
      field.trim().toUpperCase()
    ];
    
    for (const variation of variations) {
      value = row[variation];
      if (value && value !== 'N/A' && value !== '') {
        if (debug) console.log(`✅ Variation match for "${field}" -> "${variation}":`, value);
        return value;
      }
    }
    
    // Try with trailing space variations (critical for CSV import issues)
    const spaceVariations = [
      field + ' ',
      field + '  ',
      ' ' + field,
      '  ' + field
    ];
    
    for (const spaceVar of spaceVariations) {
      value = row[spaceVar];
      if (value && value !== 'N/A' && value !== '') {
        if (debug) console.log(`✅ Space variation match for "${field}" -> "${spaceVar}":`, value);
        return value;
      }
    }
    
    // Try custom variations
    for (const customVar of customVariations) {
      value = row[customVar];
      if (value && value !== 'N/A' && value !== '') {
        if (debug) console.log(`✅ Custom variation match for "${field}" -> "${customVar}":`, value);
        return value;
      }
    }
  }
  
  if (debug) console.log(`❌ No match found for fields:`, possibleFields);
  return null;
};

/**
 * Check if a row has valid data for any of the specified fields
 * 
 * @param row - The data row object
 * @param possibleFields - Array of possible field names to match
 * @param options - Optional configuration
 * @returns True if any field has valid data
 */
export const hasValidFieldValue = (
  row: any, 
  possibleFields: readonly string[] | string[], 
  options: ColumnMatchOptions = {}
): boolean => {
  const value = getFieldValue(row, possibleFields, options);
  return value !== null && value !== undefined && value !== 'N/A' && value !== '';
};

/**
 * Get all available column names from a row (useful for debugging)
 * 
 * @param row - The data row object
 * @returns Array of column names
 */
export const getAvailableColumns = (row: any): string[] => {
  return Object.keys(row || {});
};

/**
 * Find the best matching column name for a given field
 * 
 * @param row - The data row object
 * @param targetField - The field name to match
 * @param options - Optional configuration
 * @returns The best matching column name or null
 */
export const findBestColumnMatch = (
  row: any, 
  targetField: string, 
  options: ColumnMatchOptions = {}
): string | null => {
  const availableColumns = getAvailableColumns(row);
  const { debug = false } = options;
  
  // Try exact match first
  if (availableColumns.includes(targetField)) {
    if (debug) console.log(`✅ Exact column match found: "${targetField}"`);
    return targetField;
  }
  
  // Try variations
  const variations = [
    targetField.toLowerCase(),
    targetField.toUpperCase(),
    targetField.trim(),
    targetField.trim().toLowerCase(),
    targetField.trim().toUpperCase()
  ];
  
  for (const variation of variations) {
    if (availableColumns.includes(variation)) {
      if (debug) console.log(`✅ Variation column match found: "${targetField}" -> "${variation}"`);
      return variation;
    }
  }
  
  // Try with trailing space variations
  const spaceVariations = [
    targetField + ' ',
    targetField + '  ',
    ' ' + targetField,
    '  ' + targetField
  ];
  
  for (const spaceVar of spaceVariations) {
    if (availableColumns.includes(spaceVar)) {
      if (debug) console.log(`✅ Space variation column match found: "${targetField}" -> "${spaceVar}"`);
      return spaceVar;
    }
  }
  
  if (debug) console.log(`❌ No column match found for: "${targetField}"`);
  return null;
};

/**
 * Validate that all required fields are available in the data
 * 
 * @param rows - Array of data rows
 * @param requiredFields - Array of required field names
 * @param options - Optional configuration
 * @returns Object with validation results
 */
export const validateRequiredFields = (
  rows: any[], 
  requiredFields: readonly string[] | string[], 
  options: ColumnMatchOptions = {}
): {
  isValid: boolean;
  missingFields: string[];
  availableFields: string[];
  recommendations: string[];
} => {
  const { debug = false } = options;
  const availableFields = rows.length > 0 ? getAvailableColumns(rows[0]) : [];
  const missingFields: string[] = [];
  const recommendations: string[] = [];
  
  for (const field of requiredFields) {
    const bestMatch = findBestColumnMatch(rows[0] || {}, field, { debug: false });
    if (!bestMatch) {
      missingFields.push(field);
      
      // Generate recommendations
      const similarFields = availableFields.filter(col => 
        col.toLowerCase().includes(field.toLowerCase()) ||
        field.toLowerCase().includes(col.toLowerCase())
      );
      
      if (similarFields.length > 0) {
        recommendations.push(`Field "${field}" not found. Similar fields: ${similarFields.join(', ')}`);
      }
    }
  }
  
  if (debug) {
    console.log('🔍 Field validation results:', {
      requiredFields,
      availableFields,
      missingFields,
      recommendations
    });
  }
  
  return {
    isValid: missingFields.length === 0,
    missingFields,
    availableFields,
    recommendations
  };
};

// Export commonly used field combinations
export const COMMON_FIELD_COMBINATIONS = {
  WEBSITE_ENGAGEMENT: ['Website Engagement', 'Website Activity', 'Web Engagement', 'Website Visitors'],
  BUYING_INTENT: ['Buying Intent', 'Buying Intent Score', 'Intent Score', 'Purchase Intent'],
  CONTACT_STATUS: ['Contact Status', 'Status', 'Lead Status', 'Contact State'],
  CALL_OUTCOME: ['Call Outcome', 'Call Result', 'Outcome', 'Call Status'],
  LEAD_QUALIFICATION: ['Lead Qualification', 'Qualification', 'Lead Score', 'Qualification Score'],
  TIME_BASED: ['Last 7 Days', 'Last 30 Days', 'Last 90 Days', '7 Days', '30 Days', '90 Days']
} as const;
